export const RATE_WINDOW_MS = 60 * 1000;
export const MAX_SAMPLES = 720;
export const SAVED_BANNER_MS = 1 * 60 * 1000;
export const MIN_FLOW_GPM = 1;
export const DIRECTION_THRESHOLD_GAL = 5;

// 0 is a valid reading, so only null/undefined/""/NaN are treated as missing.
export const toNumber = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export function getDirection({ pressureMode, baseline, gallons }) {
  if (pressureMode === "vacuum") return "pick";
  if (pressureMode === "pressure") return "drop";
  const start = toNumber(baseline);
  const now = toNumber(gallons);
  if (start != null && now != null) {
    if (now - start > DIRECTION_THRESHOLD_GAL) return "pick";
    if (now - start < -DIRECTION_THRESHOLD_GAL) return "drop";
  }
  return "open";
}

export function flowRateGpm(samples, windowMs = RATE_WINDOW_MS) {
  if (!samples || samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  const inWindow = samples.filter((sample) => sample.t >= last.t - windowMs);
  if (inWindow.length < 2) return 0;
  const minutes = (last.t - inWindow[0].t) / 60000;
  return minutes > 0 ? (last.g - inWindow[0].g) / minutes : 0;
}

export function etaMs({ gpm, gallons, capacity }) {
  if (!Number.isFinite(gpm) || Math.abs(gpm) < MIN_FLOW_GPM) return null;
  const current = toNumber(gallons);
  if (current == null) return null;
  if (gpm > 0) {
    const max = toNumber(capacity);
    return max == null ? null : Math.max(0, ((max - current) / gpm) * 60000);
  }
  return Math.max(0, (current / -gpm) * 60000);
}

export function transferDurationMs(transfer) {
  const start = Date.parse(transfer?.startedAt);
  const stop = Date.parse(transfer?.stoppedAt);
  return Number.isFinite(start) && Number.isFinite(stop) ? Math.max(0, stop - start) : null;
}

export function signedGallons(transfer) {
  const gallons = Math.abs(toNumber(transfer?.gallonsTransferred) ?? 0);
  if (transfer?.type === "pick") return gallons;
  if (transfer?.type === "drop") return -gallons;
  return 0;
}

export function formatDuration(ms) {
  if (ms == null || !Number.isFinite(ms)) return "--";
  const seconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours
    ? `${hours}h ${String(minutes).padStart(2, "0")}m`
    : `${minutes}:${String(rest).padStart(2, "0")}`;
}

export function formatGallons(value, { signed = false } = {}) {
  const number = toNumber(value);
  if (number == null) return "--";
  const rounded = Math.round(number);
  const sign = signed && rounded > 0 ? "+" : signed && rounded < 0 ? "−" : "";
  return `${sign}${Math.abs(rounded).toLocaleString()} gal`;
}

// Decides which state the banner shows; alerts override the transfer state.
export function getBannerModel({ session, lastSaved, audit, isLive, now }) {
  const model = {
    tone: "idle",
    gallons: null,
    official: false,
    elapsedMs: null,
    gpm: null,
    eta: null,
    etaLabel: "ETA",
    transfer: null,
  };

  if (session?.status === "active") {
    Object.assign(model, {
      tone: session.direction,
      gallons: session.moved,
      elapsedMs: session.startedAt ? now - Date.parse(session.startedAt) : null,
      gpm: Math.abs(session.gpm) >= MIN_FLOW_GPM ? Math.abs(session.gpm) : null,
      eta: session.eta,
      etaLabel: session.gpm > 0 ? "To full" : "To empty",
    });
  } else if (session?.status === "saving") {
    Object.assign(model, {
      tone: "saving",
      gallons: session.moved,
      elapsedMs: session.startedAt && session.endedAt ? session.endedAt - Date.parse(session.startedAt) : null,
    });
  } else if (lastSaved && now - lastSaved.savedAt < SAVED_BANNER_MS) {
    const transfer = lastSaved.transfer;
    const duration = transferDurationMs(transfer);
    Object.assign(model, {
      tone: "saved",
      gallons: signedGallons(transfer),
      official: true,
      elapsedMs: duration,
      gpm: duration ? Math.abs(transfer.gallonsTransferred ?? 0) / (duration / 60000) : null,
      etaLabel: "Avg rate",
      transfer: transfer,
    });
  }

  if (audit?.manualCaptureMissing) model.tone = "unlogged";
  else if (!isLive) model.tone = "offline";
  return model;
}
